import { auditFindingLabel, auditTallySentence } from './auditReview'
import { mathematicalProgressText } from './mathSession'
/**
 * The research report, as data: every section, in order, with every sentence.
 *
 * ONE derivation for both report formats. The markdown report and the PDF used to
 * share only the arithmetic (D63) and each wrote its own sections around it, so a
 * section moved in one and not the other, and the two drifted sentence by
 * sentence. Now `reportDocument` decides the order, the membership and the words,
 * and `markdown.ts` and `pdfReport.ts` only dress it: one escapes for Markdown,
 * the other sets type. Neither can hold a section the other does not.
 *
 * The document opens as a research brief — where each branch stands, what was
 * measured, what sources say, where records pull apart, what held up and what
 * did not survive, what rests on unchecked assumptions, what is still open — and
 * moves the shape counts, the calibration tables and the methods to an appendix.
 * Three rules hold for everything below:
 *
 *  - Mechanical. Pure over a `Session` plus the tables passed in; no model is
 *    asked anything, nothing reads storage, and every membership is a stored
 *    field or a count over structure. Nothing is ordered by a self-rating: where
 *    an order is needed it is arrival, or how many claims sit beneath a node.
 *  - Only admitted sources. Every source named is one the citation gate kept
 *    (invariant 1); a check whose citations were all set aside names nothing.
 *  - "Refuted" only where the record says it: a stored `refuted` reading, which
 *    `refutedIsEarned` gated on the way in and on the way off disk, or a result
 *    somebody recorded as going against the claim. An objection the model raised
 *    against its own graph is an objection that stood, in reasoning.
 *
 * The block vocabulary is the PDF's, because it is the richer of the two; the
 * markdown renderer maps each block to its plain-text form.
 */

import type {
  BridgeEnd,
  FieldSurvey,
  FieldSweep,
  Outcome,
  ReasonNode,
  Session,
  SourceReading,
  SurveySource,
  Verification,
} from './types'
import {
  BEARING_LABEL,
  DIRECTIVE_LABEL,
  LENS_LABEL,
  READING_LABEL,
  auditLive,
  auditTally,
  checkedAgainstSources,
  modelNames,
  modelsUsed,
  nextStepsOf,
  readingCaveat,
  readingNote,
  refutedBacking,
  reportedResult,
  retractedWorks,
  runPassShare,
  sourceReach,
  totalSpend,
  verificationLive as coreVerificationLive,
} from './types'
import {
  CONJECTURE_MOVE_LABEL,
  RESULT_READING,
  conjecturesOf,
  consensusNodeOf,
  predictionSentence,
  registrationLive,
  throughSoFar,
} from './conjecture'
import {
  laterStatements,
  latestPriorArt,
  priorArtLabel,
  priorArtLive,
  priorArtNote,
} from './priorArt'
import {
  EXECUTED_NOT_A_RESULT,
  conjectureContracts,
  executedResults,
  executedSentence,
} from './executableConjecture'
import { SWEEP_KIND_LABEL, latestSweep, sweepItemById, sweepSentence } from './sweep'
import { canonicalUrl, hostOf } from './url'
import { decodeEntities } from './entities'
import { textTruncationNotes } from './textRetention'
import { SURVEY_PARTS, SURVEY_PART_LABEL, questionHash, surveyEvents, surveyLive } from './survey'
import { surveyCounts } from './recordDigest'
import { liveOrigin, liveOriginName, liveOriginUrl } from './sources'
import { PRICE_READ_ON, estimateSpendUsd, fmtUsd } from './models'
import { PANEL_VERDICT_LABEL, panelSplit, stabilityLine } from './panel'
import type { ModelStability } from './panel'
import { trackRecordLine, type TrackRecordRow } from './trackRecord'
import { modelKey, scoredShortfall, verdictOf } from './calibration'
import { measuredClaim } from './markdown'
import { BRIDGE_RUNGS_TOTAL, RUNG_LABEL, STOP_LABEL, bridgeStanding, sortBridges } from './rung'
import type { ModelCalibration } from './calibration'
import { refutedUnder, taintMap } from './taint'
import { writtenAt } from './rewrite'
import { childrenIndex } from './store'
import {
  dependencyChildren,
  diffVerifications,
  orphanedVerificationEntries,
  verificationEvents,
} from './rebase'
import { priorArtOf, priorArtText } from './priorWork'
import {
  DIMENSION_LABEL,
  VERDICT_GLOSS,
  comparisonStale,
  latestComparisons,
  scopeReasons,
  topTests,
  unspecifiedCount,
} from './disagreement'
import {
  CLOSENESS_WORD,
  COMPARISON_VERDICT_LABEL,
  DESIGN_WORD,
  FOUND_CONJECTURES_WORD,
  STATUS_WORD,
  citesRetractedCount,
} from './vocabulary'
import { heldUpBy } from './heldUp'
import { STILL_OPEN_ORDER, stillOpenAs } from './stillOpen'
import { FOUND_SECTION, FOUND_SECTION_TITLE } from './runFound'
import { calendarDay } from './dates'
import { briefLateNote, briefLeadClause } from './brief'
import { claimReports, designTally, evidenceClause } from './claimEvidence'
import {
  ENDING_LABEL,
  FRAME_NOTE,
  OBJECTION_STOOD_BELOW,
  endingOf,
  endingSummary,
  objectionsStoodUnder,
} from './endings'

/* ── the block model ──────────────────────────────────────────────────────
   The document as data, before any font or escape exists. Moved here from the
   PDF, which still re-exports it: its tests assert on blocks, and so do these. */

export type RunStyle = 'plain' | 'bold' | 'italic' | 'quiet' | 'mono' | 'link'

export interface TextRun {
  text: string
  style?: RunStyle
}
export type Rich = TextRun[]

export type ChipTone = 'teal' | 'amber' | 'purple' | 'red' | 'gray'

export interface Chip {
  text: string
  tone: ChipTone
}

/** One named source on a card. `url` makes the title a live link. `passage`
 *  marks a cite from a document rather than a web page, which the markdown
 *  report sets in bold rather than as a bare link. */
export interface SourceLine {
  title: string
  url?: string
  note: string
  passage?: true
}

export type Block =
  | { b: 'eyebrow'; text: string; tone: 'accent' | 'quiet' }
  | { b: 'title'; text: string }
  | { b: 'lede'; rich: Rich }
  | { b: 'counts'; items: { dot?: ChipTone; text: string }[] }
  /** `part` marks a heading above a group of sections — the appendix — which
   *  markdown sets one level up. */
  | { b: 'h2'; text: string; part?: true }
  | { b: 'h3'; text: string }
  | { b: 'para'; rich: Rich; tone?: 'body' | 'quiet' }
  | { b: 'bullet'; rich: Rich; sub?: Rich[] }
  | { b: 'quotePanel'; paras: Rich[] }
  | {
      b: 'card'
      score?: string
      scoreCaption?: string
      title: string
      chips: Chip[]
      paras: Rich[]
      sources?: SourceLine[]
      /** The node a card is about, where it is one — data for a surface that can
       *  act on it, never a word: neither format prints it. The Overview's
       *  conjecture filters read it to hide a card whole (D288). */
      node?: string
    }
  | { b: 'table'; head: string[]; rows: string[][] }
  | { b: 'ledger'; title: string; bullets: Rich[] }
  | { b: 'rule' }
  | { b: 'footer'; text: string }

const plain = (text: string): TextRun => ({ text })
const bold = (text: string): TextRun => ({ text, style: 'bold' })
const ital = (text: string): TextRun => ({ text, style: 'italic' })
const quiet = (text: string): TextRun => ({ text, style: 'quiet' })
const mono = (text: string): TextRun => ({ text, style: 'mono' })

/** The reader's calendar day — the day the app shows for the same record. */
const fmt = calendarDay

/** A title or a claim inside a line is one line, and a seed can hold several. */
const oneLine = (s: string): string => s.replace(/\s*\n+\s*/g, ' ').trim()

const s = (n: number, one = '', many = 's'): string => (n === 1 ? one : many)

/* ── the shared arithmetic ────────────────────────────────────────────────
   Moved here from `markdown.ts`, which re-exports it for the full record and the
   briefing. Every count either report prints comes out of these. */

/**
 * Compact, format-neutral notes over the append-only evidence ledger.
 *
 * Human reports keep the current verification in its existing prominent block.
 * These lines disclose the retained record without repeating every old source
 * card; the machine manifest carries each event in full.
 */
export function evidenceLedgerNotes(n: ReasonNode): string[] {
  const events = verificationEvents(n)
  const history = events.slice(0, -1)
  const out: string[] = []

  if (history.length) {
    const visible = history.slice(-3)
    const omitted = history.length - visible.length
    const rows = visible.map((v) => {
      const cited = v.sources.length + (v.corpus?.cites.length ?? 0)
      const label = v.labelAtCheck ? ` for “${v.labelAtCheck.replace(/\s+/g, ' ').trim()}”` : ''
      return `${fmt(v.checkedAt)} — ${READING_LABEL[v.reading].toLowerCase()}${label}, ${cited} cited item${cited === 1 ? '' : 's'}, ${v.model.name}`
    })
    out.push(
      `${events.length} source checks retained. Earlier records: ${rows.join('; ')}${omitted ? `; ${omitted} older ${omitted === 1 ? 'record is' : 'records are'} carried in the audit manifest` : ''}.`,
    )

    const current = events.at(-1)
    const previous = history.at(-1)
    if (current && previous) {
      const diff = diffVerifications(previous, current)
      const changes: string[] = []
      if (diff.readingChanged) {
        changes.push(
          `reading changed from ${READING_LABEL[previous.reading].toLowerCase()} to ${READING_LABEL[current.reading].toLowerCase()}`,
        )
      }
      if (diff.claimChanged) changes.push('checked wording changed')
      if (diff.modelChanged) changes.push('checking model changed')
      if (diff.scopeChanged) changes.push(`scope changed to ${current.scope}`)
      if (diff.partialChanged) changes.push('search-limit status changed')
      if (diff.countsChanged) changes.push('result or corpus counts changed')
      if (diff.coverageChanged) changes.push('document retrieval coverage changed')
      const delta = (
        label: string,
        d: { added: unknown[]; removed: unknown[]; changed: unknown[] },
      ) => {
        const bits = [
          d.added.length ? `${d.added.length} added` : '',
          d.removed.length ? `${d.removed.length} absent later` : '',
          d.changed.length ? `${d.changed.length} changed` : '',
        ].filter(Boolean)
        if (bits.length) changes.push(`${label}: ${bits.join(', ')}`)
      }
      delta('sources', diff.sources)
      delta('documents', diff.corpus)
      delta('prior work', diff.priorArt)
      delta('queries', diff.queries)
      delta('failures', diff.failures)
      delta('notices', diff.notices)
      if (diff.summaryChanged) changes.push('summary changed')

      const shown = changes.slice(0, 5)
      const extra = changes.length - shown.length
      out.push(
        changes.length
          ? `Latest recheck delta: ${shown.join('; ')}${extra ? `; ${extra} more recorded ${extra === 1 ? 'change' : 'changes'}` : ''}.`
          : 'Latest recheck delta: no recorded field changed.',
      )
    }
  }

  const rebases = n.evidenceRebases ?? []
  if (rebases.length) {
    const latest = rebases.at(-1)!
    out.push(
      `${rebases.length} ${rebases.length === 1 ? 'rethink' : 'rethinks'} after a changed check retained. Latest: rethought on ${fmt(latest.rebasedAt)} against the source record dated ${fmt(latest.verificationAt)}; ${latest.affected.length} dependent claim${latest.affected.length === 1 ? '' : 's'} in scope, ${latest.produced.length} new claim${latest.produced.length === 1 ? '' : 's'} authored in operation ${latest.opId}. Prior branches remain in the record.`,
    )
  }

  return out
}

const compactArchiveText = (text: string, limit: number): string => {
  const one = text.replace(/\s+/g, ' ').trim()
  return one.length > limit ? `${one.slice(0, limit - 1).trimEnd()}…` : one
}

/**
 * One compact, format-neutral line per retained check whose claim card is gone.
 *
 * The human reports disclose enough to identify the checked wording, reading,
 * date and provenance without reproducing a removed branch or every source card.
 * The audit manifest carries the full records.
 */
export function removedVerificationNotes(session: Session): string[] {
  return orphanedVerificationEntries(session).map(({ nodeId, verification: v }) => {
    const label = compactArchiveText(v.labelAtCheck || nodeId, 100)
    const claim = v.claimAtCheck
      ? ` Checked wording: “${compactArchiveText(v.claimAtCheck, 240)}”`
      : ' Checked wording is unavailable in this legacy record.'
    const cited = v.sources.length + (v.corpus?.cites.length ?? 0)
    const summary = v.summary ? ` ${compactArchiveText(v.summary, 180)}` : ''
    return `${label} [${nodeId}] — ${READING_LABEL[v.reading].toLowerCase()}, checked ${fmt(v.checkedAt)} with ${v.model.name}; ${cited} cited item${cited === 1 ? '' : 's'}.${claim}${summary}`
  })
}

export function removedVerificationIntro(count: number): string {
  return `${count} retained source-check ${count === 1 ? 'record belongs' : 'records belong'} to ${count === 1 ? 'a claim card' : 'claim cards'} no longer in the graph. ${count === 1 ? 'It is' : 'They are'} audit history, not evidence for a surviving claim; the manifest carries the full ${count === 1 ? 'record' : 'records'}.`
}

export interface Row {
  node: ReasonNode
  depth: number
  num: string
}

export interface Walk {
  rows: Row[]
  numOf: Map<string, string>
  detached: ReasonNode[]
}

/** The tree in arrival order, numbered by position — the order every document
 *  reads the argument in. */
export function walk(session: Session): Walk {
  const kids = childrenIndex(session)
  const rootId = session.order.find((id) => session.nodes[id]?.parentId === null)
  const rows: Row[] = []
  const numOf = new Map<string, string>()
  const visited = new Set<string>()
  if (!rootId) return { rows, numOf, detached: [] }

  // Iterative, and children in insertion order, so the document reads in the
  // order the reasoning actually arrived.
  const stack: Array<{ id: string; depth: number; num: string }> = [
    { id: rootId, depth: 0, num: '' },
  ]
  while (stack.length) {
    const f = stack.pop()!
    if (visited.has(f.id)) continue
    visited.add(f.id)
    const node = session.nodes[f.id]
    if (!node) continue
    rows.push({ node, depth: f.depth, num: f.num })
    if (f.num) numOf.set(f.id, f.num)
    const cs = kids.get(f.id) ?? []
    for (let i = cs.length - 1; i >= 0; i--) {
      const cid = cs[i]!
      const num = f.num ? `${f.num}.${i + 1}` : String(i + 1)
      stack.push({ id: cid, depth: f.depth + 1, num })
    }
  }

  const detached: ReasonNode[] = []
  for (const id of session.order) {
    if (visited.has(id)) continue
    const n = session.nodes[id]
    if (n) detached.push(n)
  }
  return { rows, numOf, detached }
}

/** How much of the argument rests on a node: how many nodes sit beneath it.
 *  A fact about structure, and the only ordering this report uses besides
 *  arrival. */
export function subtreeSizes(session: Session): Map<string, number> {
  const kids = childrenIndex(session)
  const out = new Map<string, number>()
  for (let i = session.order.length - 1; i >= 0; i--) {
    const id = session.order[i]!
    let n = 0
    for (const c of kids.get(id) ?? []) n += 1 + (out.get(c) ?? 0)
    out.set(id, n)
  }
  return out
}

/**
 * Facts about the SHAPE of a graph.
 *
 * Every one is a count over structure and two flags — no ordering by anything the
 * model rated, no threshold, no summary of what the claims say. Each is the kind of
 * fact that reads like a conclusion and is pure arithmetic, which is why they sit
 * in the appendix now: the brief above them says where each branch stands, and
 * these say how the whole graph is built.
 */
export interface Shape {
  /** Direct children of the seed. The branches the whole argument divides into. */
  branches: ReasonNode[]
  /** Those of them closed in reasoning. Their stated reasons are the substance. */
  deadBranches: ReasonNode[]
  /** Claims a live source check refuted, whose subtrees the count below covers. */
  disputed: ReasonNode[]
  /**
   * Claims BENEATH those, counted as a union rather than summed.
   *
   * A refuted claim under another refuted claim would otherwise be counted twice and
   * the fraction could exceed the graph — which is the one arithmetic error a figure
   * printed as "N of M" cannot survive.
   */
  underDisputed: number
  /**
   * The longest downward run of claims on which nobody spent a `challenge`, and its two
   * ends.
   *
   * Null only on a graph with no claims in it, which the section is skipped for: the
   * deepest claim on any path has no children, so it can never carry a challenge child
   * and every non-empty graph therefore has a run of at least one. That floor is the
   * point rather than a flaw — a thoroughly attacked graph reports 1, and a branch
   * nothing has argued with reports its whole length.
   */
  unchallenged: { length: number; from: ReasonNode; to: ReasonNode } | null
  /** Claims with no source check, no recorded result and no cross-examination. The
   *  panel is deliberately not among the three — it is one model reading its own work
   *  again, which is the distinction this whole document is organised around. */
  unchecked: number
}

export function shapeOf(session: Session): Shape {
  const kids = childrenIndex(session)
  const rootId = session.order.find((id) => session.nodes[id]?.parentId === null)
  const body: ReasonNode[] = []
  for (const id of session.order) {
    const n = session.nodes[id]
    if (n && n.parentId !== null) body.push(n)
  }

  const branches = (rootId ? (kids.get(rootId) ?? []) : [])
    .map((id) => session.nodes[id])
    .filter((n): n is ReasonNode => !!n)

  const disputed = body.filter((n) => verifiedLive(n) && n.verification?.reading === 'refuted')
  // Down every strand the canvas draws, a synthesis's second parent included — the
  // walk `taint.ts` takes. By parent alone, a synthesis joined from a refuted
  // branch was left out here and listed in "Claims standing on something refuted"
  // a few lines up, so one document counted one refutation's reach two ways.
  const flows = dependencyChildren(session)
  const under = new Set<string>()
  for (const r of disputed) {
    const stack = [...(flows.get(r.id) ?? [])]
    while (stack.length) {
      const id = stack.pop()!
      if (under.has(id)) continue
      under.add(id)
      for (const c of flows.get(id) ?? []) stack.push(c)
    }
  }

  // A claim is CHALLENGED when some child of it was produced by the `challenge` verb.
  // Read off `origin` and never off `session.ops`, which is sliced on the way to disk —
  // a long exploration would otherwise report its oldest challenges as never made.
  const challenged = new Set<string>()
  for (const id of session.order) {
    for (const c of kids.get(id) ?? []) {
      if (session.nodes[c]?.origin === 'challenge') {
        challenged.add(id)
        break
      }
    }
  }
  // The longest unchallenged run starting at each claim, bottom-up in one pass:
  // `order` puts a parent before its children, so the reverse walk has every child
  // answered before its parent is asked.
  const run = new Map<string, { len: number; end: string }>()
  for (let i = session.order.length - 1; i >= 0; i--) {
    const id = session.order[i]!
    const n = session.nodes[id]
    if (!n || n.parentId === null) continue
    if (challenged.has(id)) {
      run.set(id, { len: 0, end: id })
      continue
    }
    let best: { len: number; end: string } | null = null
    for (const c of kids.get(id) ?? []) {
      const r = run.get(c)
      if (r && (!best || r.len > best.len)) best = r
    }
    run.set(id, { len: 1 + (best?.len ?? 0), end: best?.end ?? id })
  }
  let longest: Shape['unchallenged'] = null
  for (const n of body) {
    const r = run.get(n.id)
    if (!r || r.len < 1) continue
    if (!longest || r.len > longest.length) {
      const to = session.nodes[r.end]
      if (to) longest = { length: r.len, from: n, to }
    }
  }

  return {
    branches,
    deadBranches: branches.filter((n) => n.terminal),
    disputed,
    underDisputed: under.size,
    unchallenged: longest,
    unchecked: body.filter((n) => !n.verification && !n.outcome && !n.audit).length,
  }
}

/** The readings, loudest first. `refuted` leads because a source stating the
 *  opposite is the single most useful sentence this tool produces, and a report
 *  that buried it under agreement would be burying its own best work. */
export const READING_ORDER: SourceReading[] = [
  'refuted',
  'mixed',
  'supported',
  'nothing-found',
  'not-checkable',
  'unsourced',
]

/** Whether a check still describes the words on the node. The core rule also
 * compares the retained checked wording/hash, which timestamps alone cannot do
 * after undo restores older text. */
export function verifiedLive(n: ReasonNode): boolean {
  return coreVerificationLive(n)
}

/** Claims below a node that predate the result recorded on it. */
export function staleUnderCount(session: Session, id: string): number {
  const o = session.nodes[id]?.outcome
  if (!o) return 0
  const kids = childrenIndex(session)
  let n = 0
  const stack = [...(kids.get(id) ?? [])]
  const seen = new Set<string>()
  while (stack.length) {
    const cur = stack.pop()!
    if (seen.has(cur)) continue
    seen.add(cur)
    const node = session.nodes[cur]
    if (!node) continue
    // `writtenAt`, as `staleBelow` and the taint walk date a claim.
    if (writtenAt(node) < o.recordedAt) n++
    for (const c of kids.get(cur) ?? []) stack.push(c)
  }
  return n
}

/* ── the document ─────────────────────────────────────────────────────── */

export interface ReportOptions {
  /** Injected so output is deterministic under test. */
  now?: number
  lens?: string
  temper?: string
  /** Passed in, never read here — two people holding the same graph and ledger
   *  get the same document. See `MarkdownOptions` for what each table is. */
  calibration?: ModelCalibration[]
  checkCalibration?: ModelCalibration[]
  stability?: ModelStability[]
  /** The conjecture track record, library-wide (`core/trackRecord.ts`), for the
   *  tables' purity reason; filtered to this graph's models like them. */
  trackRecord?: TrackRecordRow[]
}

export interface ReportSection {
  /** Stable, for tests and for a renderer that needs to single one out. */
  id: string
  title: string
  /** The PDF's small caps line above the heading. Markdown has no use for it. */
  eyebrow?: string
  blocks: Block[]
}

export interface ReportDoc {
  title: string
  /** The one-line header under the title: frame · claims · checked · results ·
   *  date. The dots are the PDF's; markdown joins the text. */
  header: { dot?: ChipTone; text: string }[]
  /** What the document is, in one plain sentence. */
  lede: string
  /** The frame, the standing brief and any mathematical obligations — things
   *  that change what every section below means. */
  preamble: Block[]
  sections: ReportSection[]
  appendixIntro: string
  appendix: ReportSection[]
}

/** What the document is. Replaces the two refusals it used to open on — a reader
 *  is better served by what a thing IS than by a list of what it will not do. */
export const REPORT_LEDE =
  'This document is a record of what was argued, checked and recorded in one exploration. Readings are dated source checks, not verdicts, and anything not marked as checked or recorded is the model’s reasoning.'

export const APPENDIX_INTRO =
  'Counts over the graph’s structure, how the model’s ratings have tracked what happened, and how this document was made.'

const READING_TONE: Record<SourceReading, ChipTone> = {
  supported: 'teal',
  mixed: 'amber',
  refuted: 'red',
  'nothing-found': 'gray',
  'not-checkable': 'gray',
  unsourced: 'gray',
}

const BEARING_TONE: Record<Outcome['bearing'], ChipTone> = {
  supported: 'teal',
  refuted: 'red',
  inconclusive: 'gray',
}

/** A bearing inside a count, where the full sentence would not fit. */
const BEARING_SHORT: Record<Outcome['bearing'], string> = {
  supported: 'went the claim’s way',
  refuted: 'went against it',
  inconclusive: 'settled nothing',
}

/** How the seed was read, in the report's words — and the thread column's head's
 *  (D352), so the two cannot name one framing two ways. */
export const FRAME_WORD: Record<'claim' | 'question' | 'goal', string> = {
  claim: 'Framed as a claim',
  question: 'Framed as a question',
  goal: 'Framed as a goal',
}

/** How many open claims "still open" lists before it points at the full record.
 *  A list of every open end was a quarter to a half of every report the study
 *  read; one step per claim, and a ceiling, is what keeps it a brief. */
export const OPEN_SHOWN = 12
/** How many non-assumption load-bearing claims ride under the assumptions. */
export const RESTING_SHOWN = 5
/** Past a dozen, the taint list stops being a repair queue and becomes a second
 *  copy of the graph; the count beside it is never the capped one. */
export const TAINT_SHOWN = 12

/** Where a cited passage came from, in one clause, off the stored id. */
function passageOrigin(docId: string): string {
  const o = liveOrigin(docId)
  if (!o) return 'your own document'
  // `doi:` names no row, so the clause says the passage was fetched and stops
  // there rather than naming a service that may not have been the one.
  const from = liveOriginName(o)
  return from ? `fetched during this check from ${from}` : 'fetched during this check'
}

/** The admitted sources on a check, web pages then passages. Only what the gate
 *  kept is on the record, so only what the gate kept can be printed. A page the
 *  check labelled says what kind of evidence it is beside where it is — "nejm.org
 *  · trial · read in full" — in the card's own noun (`DESIGN_WORD`); a page it
 *  did not label says nothing, never "unclear". A label, read by nothing else. */
export function sourceLinesOf(v: Verification): SourceLine[] {
  const out: SourceLine[] = []
  for (const src of v.sources) {
    const stance =
      src.stance === 'disputes'
        ? ', disputes this'
        : src.stance === 'supports'
          ? ', supports this'
          : ''
    const kind = src.design ? ` · ${DESIGN_WORD[src.design][0]}` : ''
    out.push({
      title: src.title,
      url: src.url,
      note: `${hostOf(src.url)}${kind} · ${sourceReach(src, v)}${stance}`,
    })
  }
  for (const c of v.corpus?.cites ?? []) {
    out.push({
      title: c.docTitle,
      url: liveOriginUrl(c.docId) ?? undefined,
      note: `passage ${c.passage} (${passageOrigin(c.docId)})`,
      passage: true,
    })
  }
  return out
}

/**
 * What the evidence clause under a reading is, said once above the readings. The
 * card's hover says the same; a document has no hover. Each half only where the
 * document prints what it explains: a count, past one report; a kind, wherever
 * the check labelled a page.
 */
export function evidenceClauseNote(counted: boolean, labelled: boolean): string | null {
  const parts = [
    counted
      ? 'Under a reading, “N reports · M independent” counts copies of one artefact — the same DOI or text, near-verbatim text, or a shared trial, dataset or registry identifier — once.'
      : null,
    labelled
      ? 'A kind of evidence named beside a source is what the check read the page as: a label, not a grading.'
      : null,
  ].filter((x): x is string => x !== null)
  if (!parts.length) return null
  return `${parts.join(' ')} ${parts.length > 1 ? 'Neither changes' : 'It does not change'} the reading.`
}

/** A bridge end, named with its section number when it is local. */
function endLabel(e: BridgeEnd, session: Session, numOf: Map<string, string>): string {
  if (e.where === 'foreign') return `${e.ref.label} — from “${oneLine(e.ref.seed)}”`
  const n = session.nodes[e.nodeId]
  return n ? `${numOf.get(n.id) ?? '—'} ${n.label}` : 'a branch since pruned'
}

const CONSTRAINT_NOTE = 'blocked only by a constraint you set — it comes back if you relax it'

/** The two headings "What sources say" takes when the seed carries a live
 *  survey: one over the question's own survey, one over the claims' checks that
 *  follow it, so the checks' opening sentence is not read as the survey's. */
export const SURVEY_HEADING = 'The question itself: a survey of the field'
export const CLAIM_CHECKS_HEADING = 'The claims: checks against sources'

/** The heading over the scan's unrealised pairs in the insights section. Named
 *  because the Overview (`ui/Overview.tsx`) seats its live connections section
 *  there in place of the printed cards, and finds the seat by this heading. */
export const CANDIDATES_HEADING = 'Candidate syntheses nobody has realised'

/** The conjectures section's id. The Overview's fold is this section, with the
 *  ledger's filters over its cards, and finds it by this id. */
export const CONJECTURES_SECTION = 'conjectures'

/** The heading over the field sweeps, inside the conjectures section. */
export const SWEEPS_HEADING = 'What sweeps of the field found unsettled'

/** What an absent prior-art search means, on a conjecture's card. */
export const CONJECTURE_UNSEARCHED =
  'Whether anyone has already said this has not been searched. The proposing model was told to leave out anything it recognised, and it cannot know what it never read.'

/** How many unprinted surveys the appendix dates, newest kept — the check
 *  ledger's three, for its reason: the manifest carries every one in full. */
export const EARLIER_SURVEYS_SHOWN = 3

/** The heading over those dated lines, in the report's appendix and in the full
 *  record. */
export const EARLIER_SURVEYS_TITLE = 'Earlier surveys of the question'

/** A survey source's name. The title the search returned, or its host where the
 *  provider supplied none — the title then stands in as the URL, and the host
 *  says the same thing in fewer characters, as the seed's card does. */
export const surveySourceTitle = (src: SurveySource): string =>
  src.title === src.url ? hostOf(src.url) : decodeEntities(src.title)

/**
 * The seed's live survey, as the blocks at the top of "What sources say".
 *
 * What it is comes first, in words that carry no reading, because a survey has
 * none: a record of what one search returned about the question, not a verdict
 * on it. Then the summary, the rows under the five questions in the survey's own
 * order, and the sources the gate kept (invariant 1) by title, as links, numbered
 * as the rows cite them. Evidence is printed whole, as a check's summary is in
 * this section, and whatever the codec clipped is said beneath it.
 *
 * The full record prints these same blocks under the question. Two documents for
 * people describing one record in two wordings is how they come to disagree
 * about it, so there is one.
 */
export function surveyBlocks(sv: FieldSurvey): Block[] {
  const out: Block[] = [
    {
      b: 'para',
      rich: [
        plain(
          `What one search returned about the question itself, surveyed on ${fmt(sv.surveyedAt)} with ${sv.model.name}. It is a record of what came back, not a verdict on the question and not a check of any claim below, and an absence in it is an absence in these results rather than in the literature.`,
        ),
      ],
    },
  ]
  if (sv.summary) out.push({ b: 'para', rich: [plain(sv.summary)] })
  for (const note of textTruncationNotes(sv)) {
    out.push({ b: 'para', tone: 'quiet', rich: [ital(note)] })
  }
  const numbered = new Map(sv.sources.map((src, i) => [canonicalUrl(src.url), i + 1]))
  for (const part of SURVEY_PARTS) {
    const rows = sv.rows.filter((row) => row.part === part)
    if (!rows.length) continue
    out.push({ b: 'para', rich: [bold(SURVEY_PART_LABEL[part])] })
    for (const row of rows) {
      const cited = row.refs
        .map((ref) => numbered.get(ref))
        .filter((n): n is number => n !== undefined)
      const aside = [
        row.design ? oneLine(row.design) : '',
        cited.length ? `source${s(cited.length)} ${cited.join(', ')}` : '',
      ].filter(Boolean)
      out.push({
        b: 'bullet',
        rich: [
          plain(oneLine(row.text)),
          ...(aside.length ? [quiet(` (${aside.join(' · ')})`)] : []),
        ],
      })
    }
  }
  const counts: Rich[] = [
    [quiet(surveyCounts(sv))],
    ...(sv.failures ?? []).map((f): Rich => [quiet(f)]),
  ]
  if (!sv.sources.length) {
    out.push(...counts.map((rich): Block => ({ b: 'para', tone: 'quiet', rich })))
    return out
  }
  out.push({
    b: 'card',
    title: 'What the search returned that it rests on',
    chips: [{ text: `${sv.sources.length} source${s(sv.sources.length)} kept`, tone: 'gray' }],
    paras: counts,
    sources: sv.sources.map((src, i) => ({
      title: `[${i + 1}] ${surveySourceTitle(src)}`,
      url: src.url,
      note: `${hostOf(src.url)} · ${src.via === 'fetch' ? 'page opened' : 'search result only'}`,
    })),
  })
  return out
}

/**
 * The retained surveys the brief does not print, one dated line each.
 *
 * The brief prints one survey: the live one, of the question as it now reads.
 * A re-survey replaces the one before it in everything a later pass is handed,
 * and a superseded survey printed beside it would read as a second, current
 * account of the field. But each was a paid search that happened, whose kept
 * sources nothing else retains (D143), and a report silent about them says the
 * question was surveyed once. So they are counted and dated here — model, date,
 * what the gate kept — and never their rows or summary, which the run manifest
 * carries whole.
 */
function earlierSurveyNotes(seed: ReasonNode, printed: FieldSurvey | undefined): string[] {
  const earlier = surveyEvents(seed).filter((sv) => sv.opId !== printed?.opId)
  const current = questionHash(seed.claim)
  return earlier.slice(-EARLIER_SURVEYS_SHOWN).map((sv) => {
    const set = sv.dropped ? `, ${sv.dropped} URL${s(sv.dropped)} set aside` : ''
    const wording =
      sv.questionHashAtSurvey === current ? '' : ' It searched an earlier wording of the question.'
    return `${fmt(sv.surveyedAt)} — surveyed with ${sv.model.name}; ${sv.sources.length} returned source${s(sv.sources.length)} kept${set}.${wording}`
  })
}

/**
 * Those surveys as blocks: how many there are and why they are dated rather than
 * printed, then the dated lines — or nothing, when the document prints every
 * survey the seed carries. `doc` names the document the paragraph speaks for:
 * the report's appendix says "the brief", the full record "this record". Both
 * print the live survey of the question as it now reads and date the rest, so
 * they count and date them in one wording.
 */
export function earlierSurveyBlocks(
  seed: ReasonNode,
  printed: FieldSurvey | undefined,
  doc: string,
): Block[] {
  const unprinted = surveyEvents(seed).filter((sv) => sv.opId !== printed?.opId).length
  if (!unprinted) return []
  const older = unprinted - EARLIER_SURVEYS_SHOWN
  return [
    {
      b: 'para',
      rich: [
        plain(
          `${unprinted} survey${s(unprinted)} of the question ${unprinted === 1 ? 'is' : 'are'} retained and not printed in ${doc}, which prints only the latest survey of the question as it now reads. ${unprinted === 1 ? 'It was a search' : 'Each was a search'} that happened, so ${unprinted === 1 ? 'it is' : 'they are'} dated here rather than dropped; the run manifest carries ${unprinted === 1 ? 'it' : 'them'} in full.${older > 0 ? ` ${older === 1 ? 'The oldest is' : `The ${older} oldest are`} in the manifest only.` : ''}`,
        ),
      ],
    },
    ...earlierSurveyNotes(seed, printed).map((note): Block => ({
      b: 'bullet',
      rich: [plain(note)],
    })),
  ]
}

/** One-entry memo for `reportClaimCount`, on the identity of the two fields it
 *  reads — the store re-runs a selector on every write, a streaming delta's
 *  included, and the walk need not. */
let claimMemo: { nodes: Session['nodes']; order: Session['order']; n: number } | null = null

/**
 * How many claims the report counts: every claim the tree walk reaches, the seed
 * apart — the "N claims" its header opens with (`body` below). The Findings tab
 * prints it beside its name, so the tab and the report it opens say one number
 * (D362). A node the walk cannot reach from the seed is listed as detached, never
 * counted, here as there.
 */
export function reportClaimCount(session: Pick<Session, 'nodes' | 'order'>): number {
  if (claimMemo && claimMemo.nodes === session.nodes && claimMemo.order === session.order)
    return claimMemo.n
  const n = walk(session as Session).rows.filter((r) => r.node.parentId !== null).length
  claimMemo = { nodes: session.nodes, order: session.order, n }
  return n
}

export function reportDocument(session: Session, opts?: ReportOptions): ReportDoc {
  const now = opts?.now ?? Date.now()
  const { rows, numOf } = walk(session)
  const sizes = subtreeSizes(session)
  const kids = childrenIndex(session)
  const body = rows.filter((r) => r.node.parentId !== null)
  const rowOf = new Map(rows.map((r) => [r.node.id, r]))
  const models = modelsUsed(session)
  const mixed = models.length > 1
  const maths = !!session.research?.mathematics

  const where = (r: Row) => `${r.num} ${oneLine(r.node.label)}`
  const whereId = (id: string | null): string => {
    if (!id) return 'the seed'
    const r = rowOf.get(id)
    if (!r) return 'a claim no longer in the graph'
    return r.node.parentId === null ? 'the seed' : where(r)
  }
  const below = (id: string): string[] => {
    const out: string[] = []
    const seen = new Set<string>()
    const stack = [...(kids.get(id) ?? [])]
    while (stack.length) {
      const cur = stack.pop()!
      if (seen.has(cur) || !session.nodes[cur]) continue
      seen.add(cur)
      out.push(cur)
      for (const c of kids.get(cur) ?? []) stack.push(c)
    }
    return out
  }
  const size = (r: Row) => sizes.get(r.node.id) ?? 0
  /** Heaviest first, arrival breaking ties — never a rating. */
  const arrival = new Map(rows.map((r, i) => [r.node.id, i]))
  const heaviest = (a: Row, b: Row) =>
    size(b) - size(a) || arrival.get(a.node.id)! - arrival.get(b.node.id)!

  // The one definition of "checked" in this document: a live check that read
  // sources. `not-checkable` and `unsourced` wrote a record and checked nothing.
  const liveCheck = (n: ReasonNode): boolean => checkedAgainstSources(n) && verifiedLive(n)
  const measured = body.filter((r) => !!r.node.outcome)
  const checked = body.filter((r) => liveCheck(r.node))

  /* ── header ── */

  const header: ReportDoc['header'] = [
    { text: FRAME_WORD[session.frame ?? 'claim'] },
    { dot: 'teal', text: `${body.length} claim${s(body.length)}` },
    { dot: 'amber', text: `${checked.length} checked against sources` },
    { dot: 'purple', text: `${measured.length} recorded result${s(measured.length)}` },
    { text: fmt(now) },
  ]

  const preamble: Block[] = []
  if (session.frame === 'question' || session.frame === 'goal') {
    preamble.push({ b: 'lede', rich: [ital(FRAME_NOTE[session.frame])] })
  }
  if (session.brief?.length) {
    // "For every pass" only while it is true — see `briefLateNote`.
    preamble.push({
      b: 'para',
      rich: [bold('The standing brief'), plain(` — ${briefLeadClause(session)}`)],
    })
    for (const d of session.brief) {
      const tag =
        d.kind === 'given'
          ? 'Given (STIPULATED by the user, not established — findings resting on it evaporate if it does)'
          : DIRECTIVE_LABEL[d.kind]
      const late = briefLateNote(session, d, fmt)
      // One run, not a bold tag: the directive reads as one sentence in both
      // formats, and a reader searching for "Constraint: under $50k" finds it.
      preamble.push({
        b: 'bullet',
        rich: [plain(`${tag}: ${d.text}`), ...(late ? [quiet(` · ${late}`)] : [])],
      })
    }
  }
  const mathematics = mathematicalProgressText(session)
  if (mathematics.length) {
    preamble.push({ b: 'h2', text: 'Mathematical obligations' })
    for (const line of mathematics) preamble.push({ b: 'bullet', rich: [plain(line)] })
  }

  const sections: ReportSection[] = []

  /* ── 1 · where each branch stands ──
     The first-ring branches, in arrival order, each with a status read off stored
     fields: its own ending, how much hangs under it, what was checked in it and
     how those checks read, what was recorded in it, and how the paths below it
     ended. A path closed one ring from the seed is the most load-bearing ending
     the graph has, because its reason is an argument about the seed itself. */
  {
    const blocks: Block[] = []
    const branches = rows.filter((r) => r.depth === 1)
    const ended = branches.filter((r) => endingOf(r.node))
    const count = branches.length === 1 ? 'the one branch' : `the ${branches.length} branches`
    if (!branches.length) {
      blocks.push({ b: 'para', rich: [bold('Nothing branches off the question yet.')] })
    } else if (!ended.length) {
      blocks.push({
        b: 'para',
        rich: [
          bold(`None of ${count} off the question has reached an ending.`),
          plain(
            ' That is a fact about where the argument stopped, not evidence that the branches survive attack.',
          ),
        ],
      })
    } else {
      blocks.push({
        b: 'para',
        rich: [
          bold(
            `${ended.length === branches.length ? `Every one of ${count}` : `${ended.length} of ${count}`} off the question ${ended.length === 1 ? 'has' : 'have'} reached an ending`,
          ),
          plain(` — ${endingSummary(ended.map((r) => r.node))}.`),
        ],
      })
    }
    for (const r of branches) {
      const n = r.node
      const under = below(n.id).map((id) => session.nodes[id]!)
      const family = [n, ...under]
      const parts: string[] = []
      const e = endingOf(n)
      if (e === 'answered') parts.push(ENDING_LABEL.answered)
      else if (e) {
        parts.push(
          `${ENDING_LABEL[e]}: ${oneLine(n.terminalReason || 'Reasoned out.')}${n.terminalKind === 'constraint' ? ` (${CONSTRAINT_NOTE})` : ''}`,
        )
      }
      parts.push(
        under.length ? `${under.length} claim${s(under.length)} under it` : 'nothing under it yet',
      )
      const checks = family.filter(liveCheck)
      if (checks.length) {
        const byReading = READING_ORDER.map((reading) => {
          const k = checks.filter((x) => x.verification!.reading === reading).length
          return k ? `${READING_LABEL[reading].toLowerCase()} (${k})` : null
        }).filter(Boolean)
        parts.push(`${checks.length} checked against sources: ${byReading.join(', ')}`)
      } else {
        parts.push('nothing in it checked against sources')
      }
      const results = family.filter((x) => x.outcome)
      if (results.length) {
        const bearings = (['supported', 'refuted', 'inconclusive'] as const)
          .map((bearing) => {
            const k = results.filter((x) => x.outcome!.bearing === bearing).length
            return k ? `${BEARING_SHORT[bearing]} (${k})` : null
          })
          .filter(Boolean)
        parts.push(`${results.length} recorded result${s(results.length)}: ${bearings.join(', ')}`)
      }
      // The parent's half of an objection that stood directly under the branch,
      // said as reasoning; the endings further down are counted, and that one
      // objection is not counted twice.
      const stood = objectionsStoodUnder(session, n.id)
      if (stood.length) parts.push(OBJECTION_STOOD_BELOW)
      const deeper = endingSummary(under.filter((x) => !stood.includes(x)))
      if (deeper) parts.push(`further down, ${deeper}`)
      blocks.push({
        b: 'bullet',
        rich: [bold(where(r)), plain(` — ${oneLine(n.claim)}`)],
        sub: [[quiet(parts.join(' · '))]],
      })
    }
    sections.push({
      id: 'branches',
      title: 'Where each branch stands',
      eyebrow: 'EACH BRANCH OFF THE QUESTION',
      blocks,
    })
  }

  /* ── 2 · what was measured or reported ──
     Recorded results first, always, and said plainly when there are none:
     "nothing has been tested" is the most important sentence a reader of an
     untested exploration can be given. Then the model's reported results — a study
     it recalls — marked as memory unless a check actually read sources on them. */
  {
    const blocks: Block[] = []
    if (!measured.length) {
      blocks.push({
        b: 'para',
        rich: [
          plain(
            maths
              ? 'No measured outcomes are recorded in the observation channel. Mathematical certificates and reviewed arguments are reported separately above.'
              : 'Nothing in this exploration has been tested. Every claim below is reasoning, and where a claim was checked it was checked against what a search returned rather than against an observation.',
          ),
        ],
      })
    } else {
      blocks.push({
        b: 'para',
        rich: [
          plain(
            `${measured.length} claim${s(measured.length)} carr${measured.length === 1 ? 'ies' : 'y'} a result somebody recorded. ${maths ? 'These are recorded observations; mathematical evidence is assessed separately above.' : 'These are the only statements here that came from outside the model, and they outrank everything else in the document.'}`,
          ),
        ],
      })
      for (const r of measured) {
        const o = r.node.outcome!
        const paras: Rich[] = [
          [plain(oneLine(r.node.claim))],
          [
            bold(BEARING_LABEL[o.bearing]),
            plain(`, observed ${fmt(o.at)}${o.source ? `, from ${oneLine(o.source)}` : ''}.`),
          ],
          [ital(`“${o.observed.trim()}”`)],
        ]
        const staleKids = staleUnderCount(session, r.node.id)
        if (staleKids > 0) {
          paras.push([
            quiet(
              `${staleKids} claim${s(staleKids)} below this one ${staleKids === 1 ? 'was' : 'were'} reasoned before the result was recorded, so ${staleKids === 1 ? 'it does' : 'they do'} not account for it.`,
            ),
          ])
        }
        blocks.push({
          b: 'card',
          title: where(r),
          chips: [{ text: BEARING_LABEL[o.bearing].toLowerCase(), tone: BEARING_TONE[o.bearing] }],
          paras,
        })
      }
    }
    // Not the expectation a conjecture departs from, which is stored as evidence
    // so a check can reach it and is no study anybody reported; its conjecture's
    // card names it (D288).
    const reported = body.filter((r) => reportedResult(r.node) && !r.node.outcome)
    if (reported.length) {
      blocks.push({ b: 'h3', text: 'Results the model reported' })
      blocks.push({
        b: 'para',
        rich: [
          plain(
            `${reported.length} claim${s(reported.length)} ${reported.length === 1 ? 'is' : 'are'} a study, trial or programme the model recalls. ${reported.length === 1 ? 'It is' : 'They are'} from the model’s memory unless a check read sources on ${reported.length === 1 ? 'it' : 'them'}.`,
          ),
        ],
      })
      for (const r of reported) {
        const v = r.node.verification
        const live = !!v && verifiedLive(r.node)
        const status =
          live && (v.reading === 'supported' || v.reading === 'mixed' || v.reading === 'refuted')
            ? `a check on ${fmt(v.checkedAt)} read sources on it: ${READING_LABEL[v.reading].toLowerCase()}`
            : live && v.reading === 'nothing-found'
              ? `from the model’s memory — a check on ${fmt(v.checkedAt)} found nothing addressing it`
              : 'from the model’s memory'
        // The study the check matched to the result is its source, never prior art.
        const art = priorArtOf(r.node)
        blocks.push({
          b: 'bullet',
          rich: [bold(where(r)), plain(` — ${oneLine(r.node.claim)} `), quiet(`(${status})`)],
          ...(art ? { sub: [[quiet(priorArtText(art))]] } : {}),
        })
      }
    }
    sections.push({
      id: 'measured',
      title: 'What was measured or reported',
      eyebrow: 'WHAT CAME FROM OUTSIDE THE MODEL',
      blocks,
    })
  }

  /* ── 3 · what sources say ──
     The question's own survey first, when the seed carries a live one: the one
     search of the question itself, under its own heading and with no reading.
     Then the checks on the claims, grouped by reading, loudest first. Each entry
     names its date, because a reading is what a search returned ON a date, and
     the sources it kept. With no live survey the section is exactly what it was
     before surveys existed. */
  const seedNode = rows.find((r) => r.node.parentId === null)?.node
  const survey = seedNode && surveyLive(seedNode) ? seedNode.survey : undefined
  {
    const blocks: Block[] = []
    if (survey) {
      blocks.push({ b: 'h3', text: SURVEY_HEADING })
      blocks.push(...surveyBlocks(survey))
      blocks.push({ b: 'h3', text: CLAIM_CHECKS_HEADING })
    }
    if (!checked.length) {
      blocks.push({
        b: 'para',
        rich: [
          plain(
            maths
              ? 'No source checks are recorded for the canvas claims. Mathematical reviews and exact certificates have their own record above.'
              : 'No claim in this exploration has been checked against a source. Everything below rests on the model alone.',
          ),
        ],
      })
    } else {
      // The card's clause under each reading — how many reports, how many of them
      // independent, and what kind of evidence the check read each as — asked
      // exactly as the card asks it (`evidenceClause`). What the count and the
      // labels are is said once, here, for a reader who cannot hover the card.
      const clauses = new Map(checked.map((r) => [r.node.id, evidenceClause(session, r.node.id)]))
      const note = evidenceClauseNote(
        checked.some((r) => (claimReports(session, r.node.id)?.reports ?? 0) > 1),
        checked.some((r) => designTally(r.node).size > 0),
      )
      blocks.push({
        b: 'para',
        rich: [
          plain(
            `${checked.length} of the ${body.length} claim${s(body.length)} ${checked.length === 1 ? 'has' : 'have'} been checked. A reading is what a search returned on a date — it is not a ruling on the claim, and the sources are named so they can be disagreed with.${note ? ` ${note}` : ''}`,
          ),
        ],
      })
      for (const reading of READING_ORDER) {
        const group = checked.filter((r) => r.node.verification?.reading === reading)
        if (!group.length) continue
        blocks.push({ b: 'h3', text: READING_LABEL[reading] })
        for (const r of group) {
          const v = r.node.verification!
          const paras: Rich[] = [[plain(oneLine(r.node.claim))], [quiet(readingNote(v))]]
          const clause = clauses.get(r.node.id)
          if (clause) paras.push([quiet(clause)])
          const backing = refutedBacking(v)
          if (backing) paras.push([ital(backing)])
          const caveat = readingCaveat(v)
          if (caveat) paras.push([ital(caveat)])
          if (v.summary) paras.push([plain(v.summary)])
          // What the same search said about prior work, in the one line every
          // surface that prints a novelty prints — the title is a kept source's.
          const art = priorArtOf(r.node)
          if (art) paras.push([quiet(priorArtText(art))])
          for (const note of evidenceLedgerNotes(r.node)) paras.push([quiet(note)])
          // What did not complete, as the inspector and a survey's card print it: a
          // reading that rests on a search with a step missing says which (D326).
          for (const f of v.failures ?? []) paras.push([quiet(f)])
          paras.push([quiet(`Checked on ${fmt(v.checkedAt)} with ${v.model.name}.`)])
          const chips: Chip[] = [
            { text: READING_LABEL[v.reading].toLowerCase(), tone: READING_TONE[v.reading] },
          ]
          if (v.scope === 'corpus') chips.push({ text: 'documents only', tone: 'gray' })
          blocks.push({ b: 'card', title: where(r), chips, paras, sources: sourceLinesOf(v) })
        }
      }
    }
    sections.push({
      id: 'sources',
      title: 'What sources say',
      eyebrow: 'WHAT A SEARCH RETURNED, ON A DATE',
      blocks,
    })
  }

  /* ── 4 · where findings disagree ──
     Two records pointing different ways on one claim, or the argument holding an
     objection nobody has closed. None is settled here; each is where to look. */
  {
    const blocks: Block[] = []
    const mixedReadings = checked.filter((r) => r.node.verification!.reading === 'mixed')
    const crossed = body.filter((r) => {
      const v = r.node.verification
      const o = r.node.outcome
      if (!v || !o || !liveCheck(r.node)) return false
      return (
        (v.reading === 'supported' && o.bearing === 'refuted') ||
        (v.reading === 'refuted' && o.bearing === 'supported')
      )
    })
    const split = body.filter((r) => panelSplit(r.node.panel))
    const openObjections = body.filter(
      (r) => r.node.kind === 'contradiction' && !r.node.terminal && !endingOf(r.node),
    )
    const audited = body.filter((r) => auditLive(r.node))
    // The Lab's structured comparisons, the newest of each pair with both claims
    // still on the graph (`core/disagreement.ts`): the same record the claim
    // cards' "Why these disagree" reads, so the report and the card name one
    // verdict with one word.
    const compared = latestComparisons(session)

    if (
      !mixedReadings.length &&
      !crossed.length &&
      !split.length &&
      !openObjections.length &&
      !audited.length &&
      !compared.length
    ) {
      blocks.push({
        b: 'para',
        rich: [
          plain(
            'No mixed reading, no check pulling against a recorded result, no split panel, no open objection and no review by another provider. On a graph where little was compared, that says more about how little was compared than about agreement.',
          ),
        ],
      })
    }
    if (mixedReadings.length) {
      blocks.push({ b: 'h3', text: 'Mixed readings' })
      for (const r of mixedReadings) {
        blocks.push({
          b: 'bullet',
          rich: [bold(where(r)), plain(` — ${readingNote(r.node.verification!)}`)],
        })
      }
    }
    if (crossed.length) {
      blocks.push({ b: 'h3', text: 'A check and a result that point different ways' })
      for (const r of crossed) {
        const v = r.node.verification!
        const o = r.node.outcome!
        blocks.push({
          b: 'bullet',
          rich: [
            bold(where(r)),
            plain(
              ` — a source check on ${fmt(v.checkedAt)} read “${READING_LABEL[v.reading].toLowerCase()}”; the result recorded on ${fmt(o.recordedAt)} reads “${BEARING_LABEL[o.bearing].toLowerCase()}”. The result outranks the reading.`,
            ),
          ],
        })
      }
    }
    if (split.length) {
      blocks.push({ b: 'h3', text: 'Where separate passes disagreed' })
      blocks.push({
        b: 'para',
        rich: [
          plain(
            `${split.length} claim${s(split.length)} ${split.length === 1 ? 'was' : 'were'} read again under three disciplines, blind to each other, and the three did not land in the same place. Agreement between passes would have been agreement, not correctness — so disagreement is the more informative outcome, and it is what these rows record.`,
          ),
        ],
      })
      for (const r of split) {
        const p = r.node.panel!
        blocks.push({
          b: 'bullet',
          rich: [bold(where(r)), plain(` — ${oneLine(r.node.claim)}`)],
          sub: p.votes.map((v): Rich => [
            bold(LENS_LABEL[v.lens]),
            plain(` — ${PANEL_VERDICT_LABEL[v.verdict].toLowerCase()}: ${oneLine(v.because)}`),
          ]),
        })
      }
    }
    if (openObjections.length) {
      blocks.push({ b: 'h3', text: 'Objections still open' })
      blocks.push({
        b: 'para',
        rich: [
          plain(
            `${openObjections.length} objection${s(openObjections.length)} ${openObjections.length === 1 ? 'is' : 'are'} still open — raised against the argument and not resolved either way.`,
          ),
        ],
      })
      for (const r of openObjections) {
        blocks.push({
          b: 'bullet',
          rich: [
            bold(where(r)),
            plain(` — ${oneLine(r.node.claim)} `),
            quiet(`(raised against ${whereId(r.node.parentId)})`),
          ],
        })
      }
    }
    // What a model from another company made of it. Here, because an objection
    // it raised is a record pointing against the claim, and because the count
    // beside it is what keeps one objection from reading as the whole picture.
    if (audited.length) {
      blocks.push({ b: 'h3', text: 'Scoped reviews by another provider' })
      // The listed reviews, counted: the sentence the card's cross-examination
      // press prints too, from the one helper, so the result is read where it was
      // pressed for as well as here.
      blocks.push({
        b: 'para',
        rich: [plain(auditTallySentence(auditTally(audited.map((r) => r.node.audit!))))],
      })
      blocks.push({
        b: 'para',
        rich: [
          plain(
            'The count is arithmetic over recorded verdicts. A model review is a second opinion, not proof or refutation. Criticism of novelty or relevance can leave a mathematical statement intact. Each criticism remains proposed until its argument is checked; historical reviews have no inferred scope.',
          ),
        ],
      })
      for (const r of audited) {
        const a = r.node.audit!
        blocks.push({
          b: 'bullet',
          rich: [
            bold(where(r)),
            plain(` — ${auditFindingLabel(a)}: ${oneLine(a.note)} `),
            quiet(`(${a.model.name})`),
          ],
        })
      }
    }
    // Why two claims disagree, where somebody asked: each claim's scope as a
    // model proposed it, the verdict closed rules reached over those fields, and
    // the two cheapest tests proposed to separate them. Said as proposals — the
    // Lab's Tests tab says the same — because an extracted scope is not a
    // reviewed one and a filled protocol is not a feasible experiment.
    if (compared.length) {
      blocks.push({ b: 'h3', text: 'Why these disagree' })
      blocks.push({
        b: 'para',
        rich: [
          plain(
            `${compared.length} pair${s(compared.length)} of claims ${compared.length === 1 ? 'was' : 'were'} compared scope by scope. A model proposed each claim’s scope (population, exposure, outcome, measurement, horizon, design); closed rules, not its prose, reached each verdict and ranked the tests by their recorded effort, time and money. The scopes are unreviewed proposals and the tests are proposals, not established experiments.`,
          ),
        ],
      })
      for (const a of compared) {
        const sub: Rich[] = []
        for (const row of scopeReasons(a)) {
          sub.push([
            bold(DIMENSION_LABEL[row.dimension]),
            plain(
              `: ${oneLine(row.left) || 'unspecified'} ↔ ${oneLine(row.right) || 'unspecified'}`,
            ),
          ])
        }
        const unknown = unspecifiedCount(a)
        if (!scopeReasons(a).length && unknown) {
          sub.push([
            quiet(
              `${unknown} of ${a.differences.length} scope fields left unspecified on one side or both.`,
            ),
          ])
        }
        for (const test of topTests(a))
          sub.push([quiet('Could separate them: '), plain(oneLine(test.title))])
        if (comparisonStale(session, a)) {
          sub.push([
            quiet(
              `${STATUS_WORD.comparisonStale}: a claim’s wording or scope record moved after this comparison was made.`,
            ),
          ])
        }
        blocks.push({
          b: 'bullet',
          rich: [
            bold(whereId(a.left.nodeId)),
            plain(' ↔ '),
            bold(whereId(a.right.nodeId)),
            plain(` — ${COMPARISON_VERDICT_LABEL[a.verdict]}: ${VERDICT_GLOSS[a.verdict]} `),
            quiet(`(${fmt(a.createdAt)}${a.model ? `, ${a.model.name}` : ''})`),
          ],
          sub,
        })
      }
    }
    sections.push({
      id: 'disagree',
      title: 'Where findings disagree',
      eyebrow: 'WHERE TWO RECORDS PULL APART',
      blocks,
    })
  }

  /* ── what held up ──
     The mirror of what didn't survive, and narrower on purpose (`core/heldUp.ts`):
     only a record from outside the model's reasoning — a result that went the
     claim's way, or, where no result settled it, a live check whose sources appear
     to agree. An objection that did not stand is not here, and neither is a
     review that found none: a claim nobody has knocked down reads the same as one
     nobody has attacked. Arrival order, never a rating. A claim can be here and
     under "where findings disagree" too — a result for it and a reading against
     it — because both records are true, and the result is the one that counts. */
  {
    const blocks: Block[] = []
    const held = body.filter((r) => heldUpBy(r.node))
    if (!held.length) {
      blocks.push({
        b: 'para',
        rich: [
          plain(
            'Nothing here carries a result that went its way, and no live source check read its sources as appearing to agree. That is a fact about how much has been put in front of something outside the model, not a finding against any claim.',
          ),
        ],
      })
    } else {
      blocks.push({
        b: 'para',
        rich: [
          plain(
            `${held.length} claim${s(held.length)} held up against something outside the model: a result you recorded went its way, or, where no result settled it, a source check read its sources as appearing to agree. Each is a record of what you observed or what a search returned on a date, not a ruling that the claim is right. A claim nobody has knocked down is not counted as one that held.`,
          ),
        ],
      })
      const taints = taintMap(session)
      for (const r of held) {
        const n = r.node
        const why =
          heldUpBy(n) === 'result'
            ? `${BEARING_LABEL.supported.toLowerCase()} — observed ${fmt(n.outcome!.at)}`
            : `${READING_LABEL.supported.toLowerCase()} — checked ${fmt(n.verification!.checkedAt)} with ${n.verification!.model.name}`
        // What would make "held up" read as more than it is, said beside it: the
        // footing it was reasoned from has since been refuted, or the check cites
        // a work its publisher withdrew.
        const withdrawn = retractedWorks(n).length
        const also = [
          taints.has(n.id) ? STATUS_WORD.standsOnRefuted.toLowerCase() : null,
          withdrawn ? citesRetractedCount(withdrawn) : null,
        ].filter((x): x is string => x !== null)
        // The proposition the record was recorded ABOUT, not the one on the node
        // now. This section says a claim "held up against something outside the
        // model", which is the loudest thing the report says about anything — so
        // saying it over wording the result does not name would hand that
        // authority to a proposition nobody tested. `measuredClaim` carries both.
        const measuredAs = measuredClaim(n)
        blocks.push({
          b: 'bullet',
          rich: [bold(where(r)), plain(` — ${oneLine(measuredAs.claim)}`)],
          sub: [
            [quiet([why, ...also].join(' · '))],
            ...(measuredAs.supersededBy !== null
              ? [
                  [
                    quiet(
                      `The claim has since been rewritten: ${oneLine(measuredAs.supersededBy)}. Nobody has measured that.`,
                    ),
                  ],
                ]
              : []),
          ],
        })
      }
    }
    sections.push({
      id: FOUND_SECTION.heldUp,
      title: FOUND_SECTION_TITLE[FOUND_SECTION.heldUp],
      eyebrow: 'BORNE OUT BY YOUR RESULT, OR BY SOURCES',
      blocks,
    })
  }

  /* ── 5 · what didn't survive ──
     Split three ways, because the three are different authorities: an objection
     that stood and a path that failed are the model's judgement, in reasoning;
     "ruled out" is a record — a stored refuted reading or a result somebody
     recorded against the claim. A closed knowledge gap is not here: it is open,
     and "still open" carries it. */
  {
    const blocks: Block[] = []
    const stood = body.filter((r) => endingOf(r.node) === 'objection-stood')
    const failed = body.filter((r) => endingOf(r.node) === 'path-failed')
    const ruledOut = body.filter(
      (r) =>
        (r.node.verification?.reading === 'refuted' && verifiedLive(r.node)) ||
        r.node.outcome?.bearing === 'refuted',
    )
    if (!stood.length && !failed.length && !ruledOut.length) {
      blocks.push({
        b: 'para',
        rich: [
          plain(
            'Nothing here was closed in reasoning and nothing was refuted by sources or a result. On an exploration of this size that is worth treating with suspicion rather than as good news: it more often means nobody has attacked it yet than that it survives attack.',
          ),
        ],
      })
    } else {
      blocks.push({
        b: 'para',
        rich: [
          plain(
            'Objections that stood and paths that failed are the model’s judgement, in reasoning. What sources or your result refuted is a record — of what a search returned or what you observed, not a ruling.',
          ),
        ],
      })
    }
    if (stood.length) {
      blocks.push({ b: 'h3', text: 'Objections that stood' })
      for (const r of stood) {
        blocks.push({
          b: 'bullet',
          rich: [bold(where(r)), plain(` — ${oneLine(r.node.claim)}`)],
          sub: [
            [
              quiet(
                `Stood against ${whereId(r.node.parentId)}, in reasoning — ${oneLine(r.node.terminalReason || 'Reasoned out.')}`,
              ),
            ],
          ],
        })
      }
    }
    if (failed.length) {
      blocks.push({ b: 'h3', text: 'Paths that failed' })
      for (const r of failed) {
        const rich: Rich = [
          bold(where(r)),
          plain(` — ${oneLine(r.node.terminalReason || 'Reasoned out.')}`),
        ]
        if (r.node.terminalKind === 'constraint') rich.push(ital(` (${CONSTRAINT_NOTE})`))
        if (r.node.outcome) {
          rich.push(ital(' (a result recorded here settled nothing — see what was measured)'))
        }
        blocks.push({ b: 'bullet', rich })
      }
    }
    if (ruledOut.length) {
      // "Refuted", the word the gate and a recorded result own (`endings.ts`);
      // "ruled out" was the verdict every reading's note says the tool never gives.
      blocks.push({ b: 'h3', text: 'Refuted by sources or your result' })
      for (const r of ruledOut) {
        const v = r.node.verification
        const o = r.node.outcome
        const why: string[] = []
        if (v?.reading === 'refuted' && verifiedLive(r.node)) {
          why.push(
            `${READING_LABEL.refuted.toLowerCase()} — checked ${fmt(v.checkedAt)} with ${v.model.name}`,
          )
        }
        if (o?.bearing === 'refuted') {
          why.push(`${BEARING_LABEL.refuted.toLowerCase()} — observed ${fmt(o.at)}`)
        }
        blocks.push({
          b: 'bullet',
          rich: [bold(where(r)), plain(` — ${oneLine(r.node.claim)}`)],
          sub: [[quiet(why.join('; '))]],
        })
      }
    }
    sections.push({
      id: FOUND_SECTION.didNotSurvive,
      title: FOUND_SECTION_TITLE[FOUND_SECTION.didNotSurvive],
      eyebrow: 'CLOSED IN REASONING, OR RULED OUT',
      blocks,
    })
  }

  /* ── 6 · assumptions nobody has checked ──
     The premises the model made explicit, ordered by how many claims sit beneath
     each — how much of the argument moves if one is wrong. Never by rating. */
  {
    const blocks: Block[] = []
    const unchecked = (n: ReasonNode) => !n.outcome && !liveCheck(n)
    const allAssumptions = body.filter((r) => r.node.kind === 'assumption')
    const assumptions = allAssumptions.filter((r) => unchecked(r.node)).sort(heaviest)
    if (!allAssumptions.length) {
      blocks.push({ b: 'para', rich: [plain('The model made no assumption explicit here.')] })
    } else if (!assumptions.length) {
      blocks.push({
        b: 'para',
        rich: [
          plain(
            'Every assumption the model made explicit has been checked against sources or carries a recorded result.',
          ),
        ],
      })
    } else {
      blocks.push({
        b: 'para',
        rich: [
          plain(
            'Premises the model made explicit that nothing outside the model has tested — no source check that read sources, no recorded result. Ordered by how many claims sit beneath each, because that is how much of the argument moves if one is wrong, and never by how the model rated it.',
          ),
        ],
      })
      for (const r of assumptions) {
        const n = size(r)
        blocks.push({
          b: 'bullet',
          rich: [
            bold(where(r)),
            plain(` — ${oneLine(r.node.claim)} `),
            quiet(n ? `(${n} claim${s(n)} below it)` : '(nothing below it yet)'),
          ],
        })
      }
    }
    const resting = body
      .filter((r) => r.node.kind !== 'assumption' && unchecked(r.node) && size(r) >= 2)
      .sort(heaviest)
      .slice(0, RESTING_SHOWN)
    if (resting.length) {
      blocks.push({ b: 'h3', text: 'Other load-bearing claims nobody has checked' })
      blocks.push({
        b: 'para',
        rich: [
          plain(
            'These claims have the most reasoning built on top of them and none has been checked against anything. That is where being wrong would cost the most, and it is measured by how many claims sit beneath each one rather than by how interesting it looks.',
          ),
        ],
      })
      for (const r of resting) {
        const n = size(r)
        blocks.push({
          b: 'bullet',
          rich: [
            bold(where(r)),
            plain(` — ${oneLine(r.node.claim)} `),
            quiet(`(${n} claim${s(n)} below it)`),
          ],
        })
      }
    }
    sections.push({
      id: 'assumptions',
      title: 'Assumptions nobody has checked',
      eyebrow: 'ORDERED BY WHAT RESTS ON EACH',
      blocks,
    })
  }

  /* ── 7 · claims standing on something refuted ──
     The taint the app already computes: reasoning the record has since knocked out
     from under, not re-derived. Reachability plus two dates, never a judgement. */
  {
    const blocks: Block[] = []
    const taints = taintMap(session)
    const taintedAll = body.filter((r) => taints.has(r.node.id)).sort(heaviest)
    const tainted = taintedAll.slice(0, TAINT_SHOWN)
    const n = taintedAll.length
    if (!n) {
      blocks.push({
        b: 'para',
        rich: [
          plain(
            'Nothing here stands on a claim that a source check read as stating the opposite, or that a result you recorded went against.',
          ),
        ],
      })
    } else {
      blocks.push({
        b: 'para',
        rich: [
          plain(
            `${n} claim${s(n)} ${n === 1 ? 'was' : 'were'} reasoned from something that has since been refuted, and ${n === 1 ? 'has' : 'have'} not been re-derived since. Refuted here is a stored record — a reading the citation gate admitted as sources stating the opposite, or a result you recorded as going against the claim. This is not a finding that they are wrong — a conclusion can outlive the argument that reached it — it is a list of what is owed a second look.`,
          ),
        ],
      })
      for (const r of tainted) {
        const t = taints.get(r.node.id)!
        const under = size(r)
        const how = t.via === 'outcome' ? 'a result you recorded' : 'a source check'
        // A synthesis reached through the branch it does not hang under says which
        // claim carries it there, or "stands on 1" reads as a typo from branch 2.
        const through = refutedUnder(session, r.node.id)?.through
        blocks.push({
          b: 'bullet',
          rich: [
            bold(where(r)),
            plain(` — ${oneLine(r.node.claim)} `),
            quiet(
              `(stands on ${whereId(t.sourceId)}, refuted by ${how} ${fmt(t.at)}${through ? `, which it reaches through ${whereId(through.id)}` : ''}${under ? `; ${under} claim${s(under)} below it` : ''})`,
            ),
          ],
        })
      }
      if (n > tainted.length) {
        blocks.push({
          b: 'para',
          tone: 'quiet',
          rich: [
            ital(
              `The ${n - tainted.length} with least beneath ${n - tainted.length === 1 ? 'it is' : 'them are'} not listed here. The full record carries every one.`,
            ),
          ],
        })
      }
    }
    sections.push({ id: 'tainted', title: 'Claims standing on something refuted', blocks })
  }

  /* ── 8 · still open ──
     One next step per claim, not every step. Stated unknowns first, then results
     that settled nothing, then ends nobody has taken further — each in arrival
     order, and capped, because a list of every open end was a quarter to a half of
     every report the study read. */
  {
    const blocks: Block[] = []
    // The rule is `stillOpenAs`, which a run's end card counts by too, so its
    // "still open" line opens this list. Kinds with a section of their own are
    // read there: a reported result, an assumption, a proposed test, an open
    // objection and a conjecture are each an open end, and listing them twice
    // would make this list the long one again.
    const openAs = (r: Row) => stillOpenAs(r.node, (kids.get(r.node.id) ?? []).length > 0)
    const open = STILL_OPEN_ORDER.flatMap((as) => body.filter((r) => openAs(r) === as))
    if (!open.length) {
      blocks.push({
        b: 'para',
        rich: [
          plain(
            'Nothing here is left open: every claim has something derived from it, an ending, or a result.',
          ),
        ],
      })
    } else {
      blocks.push({
        b: 'para',
        rich: [
          plain(
            `${open.length} claim${s(open.length)} ${open.length === 1 ? 'is' : 'are'} still open: stated unknowns, results that settled nothing, and ends nobody has taken further. Each carries the first step the reasoning proposed for it, where it proposed one — one step per claim, not every step.`,
          ),
        ],
      })
      for (const r of open.slice(0, OPEN_SHOWN)) {
        const n = r.node
        const tag =
          n.kind === 'gap'
            ? n.terminal
              ? ENDING_LABEL.open
              : 'a stated unknown'
            : n.outcome?.bearing === 'inconclusive'
              ? 'your result did not settle it'
              : 'nothing derived from it yet'
        const step = nextStepsOf(n)[0]
        blocks.push({
          b: 'bullet',
          rich: [
            bold(where(r)),
            plain(' — '),
            quiet(`${tag}.`),
            plain(step ? ` Next: ${oneLine(step)}` : ' No next step was proposed.'),
          ],
        })
      }
      if (open.length > OPEN_SHOWN) {
        blocks.push({
          b: 'para',
          tone: 'quiet',
          rich: [
            ital(
              `The other ${open.length - OPEN_SHOWN} are in the full record, with every step proposed for each.`,
            ),
          ],
        })
      }
    }
    sections.push({
      id: FOUND_SECTION.open,
      title: FOUND_SECTION_TITLE[FOUND_SECTION.open],
      blocks,
    })
  }

  /* ── 9 · the model's takeaway and synthesis claims ──
     The model's own classification of its own output, near the end and named as
     such, with the pairs a scan proposed and nobody realised. The kind 'insight'
     reads Takeaway here, as everywhere the kind is named, because Insight is now
     the reader's word for a conjecture (D333); the id 'insights' stays. */
  {
    const blocks: Block[] = []
    const marked = body.filter((r) => r.node.kind === 'insight' || r.node.kind === 'synthesis')
    if (!marked.length) {
      blocks.push({
        b: 'para',
        rich: [plain('The model labelled nothing here a takeaway or a synthesis.')],
      })
    } else {
      blocks.push({
        b: 'para',
        rich: [
          plain(
            'This is the model’s own classification of its own output, not a ranking and not a verdict. Nothing here has been checked unless it also appears above.',
          ),
        ],
      })
      for (const r of marked) {
        const rich: Rich = [bold(where(r)), plain(` — ${oneLine(r.node.claim)}`)]
        const v = r.node.verification
        if (v && verifiedLive(r.node)) {
          rich.push(quiet(` (checked: ${READING_LABEL[v.reading].toLowerCase()})`))
        }
        // A takeaway is where a reader looks for something new, so what a check
        // found about prior work rides under it.
        const art = priorArtOf(r.node)
        blocks.push({ b: 'bullet', rich, ...(art ? { sub: [[quiet(priorArtText(art))]] } : {}) })
      }
    }
    if (session.bridges.length) {
      blocks.push({ b: 'h3', text: CANDIDATES_HEADING })
      blocks.push({
        b: 'para',
        rich: [
          plain(
            'Pairs of distant branches a scan proposed fusing, ordered by what each has EARNED: the highest rung of proposed → searched → realised → attacked → cross-examined → checked → observed whose every lower rung also holds, each read off records the proposing model did not write about its own proposal; the scanning model’s own rating orders nothing here, and is printed beside each as the scan hint it is.',
          ),
        ],
      })
      for (const bridge of sortBridges(session.bridges, session.nodes)) {
        const stood = bridgeStanding(bridge, session.nodes)
        blocks.push({
          b: 'card',
          score: `${stood.earned.length}/${BRIDGE_RUNGS_TOTAL}`,
          scoreCaption: (stood.stop
            ? STOP_LABEL[stood.stop]
            : RUNG_LABEL[stood.rung]
          ).toUpperCase(),
          title: oneLine(bridge.title),
          chips: [
            {
              text: endLabel(bridge.a, session, numOf),
              tone: bridge.a.where === 'foreign' ? 'purple' : 'teal',
            },
            {
              text: endLabel(bridge.b, session, numOf),
              tone: bridge.b.where === 'foreign' ? 'purple' : 'teal',
            },
            { text: `scan hint ${bridge.score.toFixed(2)}`, tone: 'gray' },
          ],
          paras: [[plain(oneLine(bridge.why))]],
        })
      }
    }
    sections.push({
      id: 'insights',
      title: 'The model’s takeaway and synthesis claims',
      eyebrow: 'THE MODEL’S OWN LABELS',
      blocks,
    })
  }

  /* ── 10 · conjectures, which the reader reads as Insights (D333) ──
     Proposals with a contract (D278), with the rest of what the reasoning
     proposes (D141): after the model's own labels, before the tests, and in the
     seat the Overview's ledger had after the connections (D284). The tab's
     Insights fold IS this section now, so the tab, the Markdown and the PDF
     cannot say two things about one conjecture (D288). Each card is what a pass
     set down and what has happened to it since: the prediction and its kill test;
     the prior-art search's reading, in its own label and note, or the word for
     none — never a word that calls a conjecture new (invariant 12); what an
     executed contract read, beside a result and never as it (D283); a result you
     recorded; the expectation it departs from, where somebody put it on the
     graph; and what it has been through on the graph around it — attacks, a
     review, a panel, tests under it — which the ledger printed and this card had
     dropped (D291). Newest registration first, the ledger's order; counted, never
     ranked (D281). Then the field sweeps a conjecture pass reads, printed as the
     reportage they are (D280). The re-read rating is left to the full record: it
     prints a self-rating, and nothing in the brief may move when one does. */
  {
    const blocks: Block[] = []
    // A conjecture off the walk has no number to be named by, as no claim does.
    const proposed = conjecturesOf(session).filter((n) => rowOf.has(n.id))
    const contracts = new Map(proposed.map((n) => [n.id, conjectureContracts(session, n)]))
    const readings = new Map(proposed.map((n) => [n.id, executedResults(session, n)]))
    if (!proposed.length) {
      blocks.push({
        b: 'para',
        rich: [plain('No insight has been proposed in this exploration.')],
      })
    } else {
      blocks.push({
        b: 'para',
        rich: [
          plain(
            `${proposed.length} insight${s(proposed.length)} ${proposed.length === 1 ? 'was' : 'were'} proposed here: ${proposed.length === 1 ? 'a claim' : 'claims'} a pass set down as stated by no source the proposing model knew, each with a prediction that would tell it apart from what it departs from. Listed by registration, newest first, with what each has been through — counted, never ranked. Whether anyone has already said one is for a prior-art search to report.`,
          ),
        ],
      })
      // Said once, where readings are listed, as the card says it once (D283).
      if (proposed.some((n) => contracts.get(n.id)!.length || readings.get(n.id)!.length)) {
        blocks.push({ b: 'para', tone: 'quiet', rich: [ital(EXECUTED_NOT_A_RESULT)] })
      }
    }
    for (const n of proposed) {
      const c = n.conjecture!
      const art = latestPriorArt(n)
      const later = art ? laterStatements(c, art) : []
      const paras: Rich[] = [
        [plain(oneLine(n.claim))],
        [quiet(`${CONJECTURE_MOVE_LABEL[c.move]} · departs from: `), plain(oneLine(c.departsFrom))],
        [bold('Predicts: '), plain(oneLine(predictionSentence(n)))],
        [bold('Killed by: '), plain(oneLine(c.killTest))],
      ]
      if (art) {
        const q = art.queries.length
        paras.push([
          quiet(
            `Prior-art search, ${fmt(art.searchedAt)}, ${q} quer${q === 1 ? 'y' : 'ies'}, by ${art.model.name}${priorArtLive(n, art) ? '' : ', of wording since rewritten'}: `,
          ),
          plain(`${priorArtLabel(art, later)}. ${priorArtNote(art, undefined, later)}`),
        ])
      } else {
        paras.push([quiet(CONJECTURE_UNSEARCHED)])
      }
      for (const r of readings.get(n.id)!) paras.push([quiet(executedSentence(r))])
      const unrun = contracts.get(n.id)!.filter((x) => !x.results.length).length
      if (unrun) {
        paras.push([
          quiet(
            `${unrun === 1 ? 'An executable test' : `${unrun} executable tests`} proposed for it ${unrun === 1 ? 'has' : 'have'} not been run; Research Lab holds ${unrun === 1 ? 'it' : 'them'}.`,
          ),
        ])
      }
      if (n.outcome) {
        paras.push([
          bold(`${RESULT_READING[n.outcome.bearing]}.`),
          plain(` Observed ${fmt(n.outcome.at)}.`),
        ])
      }
      // The counterpart of the prior-art search: whether anybody holds what it
      // departs from, where that was put on the graph for a source check.
      const carried = consensusNodeOf(session, n.id)
      const carriedRow = carried ? rowOf.get(carried.id) : undefined
      if (carried && carriedRow) {
        const v = carried.verification
        paras.push([
          quiet(
            `What it departs from is on the graph as ${where(carriedRow)}: ${v && verifiedLive(carried) ? `${READING_LABEL[v.reading].toLowerCase()}, checked ${fmt(v.checkedAt)} with ${v.model.name}` : 'no source check has read it yet'}.`,
          ),
        ])
      }
      // What it has been through on the graph around it (D291): the ledger's line,
      // less what the lines above already say. Counts and stored values, a zero
      // said rather than dropped, and never a score (D282).
      paras.push([quiet('Through so far: '), plain(`${throughSoFar(session, n).join(' · ')}.`)])
      // The model's own pointers, named as the card names them: a claim by its
      // number, a sweep item as what one page said (D280).
      if (c.anchors.length) {
        const named = c.anchors.map((id) => {
          if (rowOf.has(id)) return whereId(id)
          const hit = sweepItemById(session, id)
          return hit
            ? `${SWEEP_KIND_LABEL[hit.item.kind]} on ${hostOf(hit.item.url)}`
            : 'a record since removed'
        })
        paras.push([
          quiet(
            `Offered as explaining ${named.join('; ')} — the model’s own pointers, and nothing checks that it explains them.`,
          ),
        ])
      }
      const rewrites = c.registrations?.length ?? 0
      paras.push([
        quiet(
          `${n.model ? `Proposed by ${n.model.name}; registered` : 'Registered'} ${fmt(c.registered.at)}${rewrites ? `, after ${rewrites} earlier registration${s(rewrites)} a rewrite replaced` : ''}.`,
        ),
      ])
      if (!registrationLive(n)) {
        paras.push([ital('The wording above is not the wording that was registered.')])
      }
      // The pages the reading rests on: each one the search returned (invariant
      // 1), listed or found close by the backstop, once.
      const pages: SourceLine[] = []
      const seenPage = new Set<string>()
      for (const e of art?.entries ?? []) {
        seenPage.add(canonicalUrl(e.url))
        pages.push({
          title: e.title,
          url: e.url,
          note: [
            hostOf(e.url),
            CLOSENESS_WORD[e.closeness],
            e.quoteFound === false ? 'its quoted sentence is not on the page' : null,
            later.includes(e)
              ? `dated ${e.year} by the page itself, after this was registered`
              : null,
          ]
            .filter((x): x is string => x !== null)
            .join(' · '),
        })
      }
      for (const h of art?.backstop.hits ?? []) {
        if (seenPage.has(canonicalUrl(h.url))) continue
        seenPage.add(canonicalUrl(h.url))
        pages.push({ title: h.title, url: h.url, note: `${hostOf(h.url)} · close by term overlap` })
      }
      blocks.push({
        b: 'card',
        title: where(rowOf.get(n.id)!),
        // The reading's own label, in the grey every chip that is not a verdict
        // wears: a hue here would grade what a search returned. Where none has
        // run, the word the fold's filter for that state says, so the filter and
        // the cards under it name it one way — the ledger's card had said it in
        // its facts, and this card only in a sentence (D287, D291).
        chips: [
          {
            text: (art
              ? priorArtLabel(art, later)
              : STATUS_WORD.notSearchedForPriorArt
            ).toLowerCase(),
            tone: 'gray',
          },
        ],
        paras,
        ...(pages.length ? { sources: pages } : {}),
        node: n.id,
      })
    }
    // The newest sweep of each claim, oldest first, as the full record prints
    // them — each item what one page says is unsettled, named by its host.
    const swept = [...new Set((session.sweeps ?? []).map((x) => x.nodeId))]
      .map((id) => latestSweep(session, id))
      .filter((x): x is FieldSweep => x !== undefined)
      .sort((a, b) => a.searchedAt - b.searchedAt)
    if (swept.length) {
      blocks.push({ b: 'h3', text: SWEEPS_HEADING })
      blocks.push({
        b: 'para',
        rich: [
          plain(
            'Each item is what one page a search returned says is unsettled, quoted from it: reportage, not a finding about the field, and never a step in the reasoning. An insight pass on a claim reads the sweeps of that claim and of every premise it rests on.',
          ),
        ],
      })
      for (const sw of swept) {
        blocks.push({
          b: 'bullet',
          rich: [
            bold(
              rowOf.has(sw.nodeId)
                ? whereId(sw.nodeId)
                : `${oneLine(sw.subjectAtSweep)} (no longer on the graph)`,
            ),
            quiet(` — ${fmt(sw.searchedAt)}, ${sw.model.name}: `),
            plain(sweepSentence(sw)),
          ],
          sub: sw.items.map((i): Rich => [
            quiet(
              `${SWEEP_KIND_LABEL[i.kind].charAt(0).toUpperCase()}${SWEEP_KIND_LABEL[i.kind].slice(1)}${i.year !== undefined ? ` (${i.year})` : ''}: `,
            ),
            plain(oneLine(i.statement)),
            quiet(
              ` (${hostOf(i.url)}${i.quoteFound ? '' : '; the quote could not be checked against the page'})`,
            ),
          ]),
        })
      }
    }
    sections.push({
      id: CONJECTURES_SECTION,
      // The word the run card's line and the run account point at it by.
      title: FOUND_CONJECTURES_WORD,
      eyebrow: 'PROPOSALS, EACH WITH A PREDICTION',
      blocks,
    })
  }

  /* ── 11 · proposed tests ── */
  {
    const blocks: Block[] = []
    const tests = body.filter((r) => r.node.kind === 'experiment' && !r.node.outcome)
    if (!tests.length) {
      blocks.push({ b: 'para', rich: [plain('The reasoning has proposed no test yet.')] })
    } else {
      blocks.push({
        b: 'para',
        rich: [
          plain(
            `${tests.length} experiment${s(tests.length)} the reasoning designed and nobody has run — no result is recorded against ${tests.length === 1 ? 'it' : 'any of them'}. Recording one moves it to what was measured.`,
          ),
        ],
      })
      for (const r of tests) {
        blocks.push({ b: 'bullet', rich: [bold(where(r)), plain(` — ${oneLine(r.node.claim)}`)] })
      }
    }
    sections.push({ id: 'tests', title: 'Proposed tests', blocks })
  }

  /* ── the appendix ── */

  const appendix: ReportSection[] = []

  /* The shape of the graph, in three counts. Reachability, arrival order and flags:
     a reader holding the same graph gets the same numbers. */
  if (body.length) {
    const sh = shapeOf(session)
    const paras: Rich[] = []
    const name1 = (n: ReasonNode) => {
      const num = numOf.get(n.id)
      return `${num ? `${num} ` : ''}${oneLine(n.label)}`
    }
    paras.push(
      sh.disputed.length === 0
        ? [
            bold('No source check here found sources stating the opposite'),
            plain(
              `, so nothing in the graph descends from one. On ${body.length} claim${s(body.length)} that is as likely to mean few were checked as that none failed.`,
            ),
          ]
        : [
            bold(
              `${sh.underDisputed} of the ${body.length} claim${s(body.length)} descend from ${sh.disputed.length === 1 ? 'a claim' : 'claims'} a source check refuted`,
            ),
            plain(
              ` (${Math.round((sh.underDisputed / body.length) * 100)}% of the graph), beneath ${sh.disputed.map(name1).join(', ')}. Counted as a union, so a refutation under a refutation is counted once.`,
            ),
          ],
    )
    if (sh.unchallenged) {
      const u = sh.unchallenged
      paras.push([
        bold(`The longest chain nobody has challenged runs ${u.length} claim${s(u.length)} deep`),
        plain(
          u.length === 1
            ? `: ${name1(u.from)}. `
            : `, from ${name1(u.from)} down to ${name1(u.to)}. `,
        ),
        plain(
          'Challenge is the first verb this tool tries on anything, so a chain it never reached is a chain nothing has argued with. One is the floor — the last claim on any path has nothing below it to have been attacked from.',
        ),
      ])
    }
    paras.push([
      bold(
        `${sh.unchecked === body.length ? `Every one of the ${body.length} claim${s(body.length)}` : `${sh.unchecked} of the ${body.length} claim${s(body.length)}`} carr${sh.unchecked === 1 ? 'ies' : 'y'} nothing from outside the graph`,
      ),
      plain(
        ' — no source check, no recorded result, no cross-examination by another provider. Most claims in most explorations will be in this state; it is the normal condition and not a defect, and the number is here so it is not mistaken for a smaller one.',
      ),
    ])
    appendix.push({
      id: 'shape',
      title: 'The shape of this graph',
      eyebrow: 'COUNTS OVER STRUCTURE — NOT A VERDICT',
      blocks: [
        {
          b: 'para',
          rich: [
            plain(
              'Counts over the structure — which claim hangs off which, which verb produced it, which of the four epistemic fields is filled — so none of them is a reading of what the claims say, and none is a verdict on the idea.',
            ),
          ],
        },
        { b: 'quotePanel', paras },
      ],
    })
  }

  const removedChecks = removedVerificationNotes(session)
  if (removedChecks.length) {
    appendix.push({
      id: 'removed-checks',
      title: 'Checks on removed claims',
      eyebrow: 'RETAINED AUDIT HISTORY',
      blocks: [
        { b: 'para', rich: [plain(removedVerificationIntro(removedChecks.length))] },
        ...removedChecks.map((note): Block => ({ b: 'bullet', rich: [plain(note)] })),
      ],
    })
  }

  // Counted and dated, never printed — see `earlierSurveyNotes`.
  const earlierSurveys = seedNode ? earlierSurveyBlocks(seedNode, survey, 'the brief') : []
  if (earlierSurveys.length) {
    appendix.push({
      id: 'earlier-surveys',
      title: EARLIER_SURVEYS_TITLE,
      eyebrow: 'RETAINED AUDIT HISTORY',
      blocks: earlierSurveys,
    })
  }

  // Only the models that actually produced something here. A table for a model
  // this graph never used would be a fact about another exploration.
  const calibrated = (opts?.calibration ?? []).filter((c) =>
    models.some((m) => modelKey(m) === c.key),
  )
  const checked2 = (opts?.checkCalibration ?? []).filter((c) =>
    models.some((m) => modelKey(m) === c.key),
  )
  const steady = (opts?.stability ?? []).filter((x) => models.some((m) => modelKey(m) === x.key))
  const tested = (opts?.trackRecord ?? []).filter((r) => models.some((m) => modelKey(m) === r.key))

  const calibrationSection = (
    id: string,
    list: ModelCalibration[],
    title: string,
    intro: string,
    holdCol: string,
    kind: 'outcome' | 'checked',
    closing: string,
  ) => {
    if (!list.length) return
    const blocks: Block[] = [{ b: 'para', rich: [plain(intro)] }]
    for (const c of list) {
      blocks.push({ b: 'h3', text: c.model.name })
      if (c.brier === null) {
        // The remainder, not "too few": this table is empty for almost every
        // exploration, and a sentence that only says no is indistinguishable from
        // a feature that does not work.
        blocks.push({
          b: 'para',
          rich: [
            plain(
              `${scoredShortfall(c)}${c.inconclusive ? ` ${c.inconclusive} further ${kind === 'outcome' ? `result${s(c.inconclusive)}` : `search${s(c.inconclusive, '', 'es')}`} settled nothing, counted and never scored.` : ''}`,
            ),
          ],
        })
        continue
      }
      blocks.push({
        b: 'para',
        rich: [
          bold(`${c.supported} of ${c.scored}`),
          plain(
            kind === 'outcome'
              ? ` tested claims held up${c.inconclusive ? `, with ${c.inconclusive} more where the result settled nothing` : ''}, across ${c.explorations} exploration${s(c.explorations)}. ${verdictOf(c)}`
              : ` checked claims read as supported${c.inconclusive ? `, with ${c.inconclusive} more where the search settled nothing` : ''}, across ${c.explorations} exploration${s(c.explorations)}. ${verdictOf(c, 'checked')}`,
          ),
        ],
      })
      blocks.push({
        b: 'table',
        head: ['It said', 'On how many', holdCol],
        rows: c.bands.map((band) => [
          `${band.lo.toFixed(1)}–${band.hi.toFixed(1)} (mean ${band.claimed})`,
          String(band.scored),
          String(band.supported),
        ]),
      })
      blocks.push({
        b: 'para',
        tone: 'quiet',
        rich: [
          ital(
            `Brier ${c.brier} against ${c.baseline} for a rating that ignored the claim and always said the base rate. Lower is better; the comparison is the point, because the score alone is unreadable without it.`,
          ),
        ],
      })
    }
    blocks.push({ b: 'para', tone: 'quiet', rich: [ital(closing)] })
    appendix.push({ id, title, blocks })
  }

  /* Two tables, never merged: a recorded result outranks a reading, and an
     average of the two would answer neither question. */
  calibrationSection(
    'calibration',
    calibrated,
    'How well its confidence has tracked reality',
    'Every confidence in this document is the model rating its own work. These figures are the only check on that: they compare what it claimed, before any result existed, against what was actually observed — across every exploration in the library it was built from, not only this one. Explorations moved to the archive are not counted until they are opened again, so this is a floor.',
    'How many held up',
    'outcome',
    "These are the claims somebody chose to test, which is not a random sample of the graph — people test what they doubt and what is cheap to test. Read it as this model's record on your questions rather than as its calibration in general, and note that self-ratings do not compare across models.",
  )
  calibrationSection(
    'check-calibration',
    checked2,
    'How well its confidence has tracked what sources said',
    'The same comparison as above, over a weaker kind of evidence and kept apart from it for that reason. A reading is what a search returned on a date, not an observation — but it comes from outside the reasoning pass and its citations were checked against what the search actually returned, so it can grade a confidence that was written before it existed.',
    'How many sources supported',
    'checked',
    `Two biases, and neither is correctable here. Which claims get checked is chosen by structure — what the rest of the argument rests on, then what is most open — so this is not a random sample either. And “${READING_LABEL.supported.toLowerCase()}” is a record of what came back, so a claim scored as holding up has not been shown to be true.`,
  )

  /* How its insights fared when tested (D282; the reader's word since D333). Counts, never a score, and after
     the two calibration tables because it answers a different question: not whether
     a confidence tracked reality, but how predictions went where somebody chose to
     test them. */
  if (tested.length) {
    const blocks: Block[] = [
      {
        b: 'para',
        rich: [
          plain(
            "Each insight in a model's graphs set down a prediction before anything answered it. These are the ones somebody recorded a result against — counted per model across the library, never scored, and never printed below the calibration threshold.",
          ),
        ],
      },
    ]
    for (const r of tested) {
      blocks.push({ b: 'h3', text: r.model.name })
      blocks.push({ b: 'para', rich: [plain(trackRecordLine(r))] })
    }
    blocks.push({
      b: 'para',
      tone: 'quiet',
      rich: [
        ital(
          "You chose which insights to test, so this is a record of those and not a rate at which a model's insights hold. A result under wording registered after it, or on an insight rewritten by hand, is counted apart.",
        ),
      ],
    })
    appendix.push({ id: 'track-record', title: 'How its insights fared when tested', blocks })
  }

  /* Steadiness, which is not accuracy in either direction. */
  if (steady.length) {
    const blocks: Block[] = [
      {
        b: 'para',
        rich: [
          plain(
            'A different question from the two above, and the reason it is kept apart from them: those compare what the model claimed against something outside it, while this counts how far three blind passes of the model itself landed apart. Every panel in the library is counted, not only this exploration’s — and, like the tables above, not the ones inside archived explorations until those are opened again. Nothing was asked how much it agreed — the verdicts are counted, and no pass’s rating of its own verdict enters this at all.',
          ),
        ],
      },
    ]
    for (const x of steady) {
      blocks.push({ b: 'h3', text: x.model.name })
      blocks.push({ b: 'para', rich: [plain(stabilityLine(x))] })
    }
    blocks.push({
      b: 'para',
      tone: 'quiet',
      rich: [
        ital(
          'A model that agrees with itself is a model that is consistent, which is worth knowing and is not evidence of anything being true — three passes of one model converging on a mistake look exactly like this. The divergences are the informative half, and they are listed above under where separate passes disagreed.',
        ),
      ],
    })
    appendix.push({ id: 'stability', title: 'How often it agreed with itself', blocks })
  }

  /* Provenance. The counts are measured; the dollar figure beside them is an
     estimate, and it keeps its own sentence so it cannot read as one of them. */
  {
    const blocks: Block[] = []
    const spent = totalSpend(session)
    const runs = session.runs.length
    // Passes, not run records, in the total's own frame — `runPassShare` holds
    // both halves of that promise.
    const runPasses = runPassShare(session)
    const bits: Rich = [
      mono(`${body.length} claim${s(body.length)}`),
      plain(' across '),
      mono(`${session.ops.length} model pass${s(session.ops.length, '', 'es')}`),
    ]
    if (models.length) bits.push(plain(`, reasoned with ${modelNames(models)}`))
    if (runs) {
      bits.push(
        plain(
          `. ${runPasses} of the passes came from ${runs === 1 ? 'a run that went on its own' : `${runs} runs that went on their own`}`,
        ),
      )
    }
    bits.push(plain('. '))
    bits.push(mono(`${spent.inputTokens.toLocaleString()}`), plain(' input and '))
    bits.push(mono(`${spent.outputTokens.toLocaleString()}`), plain(' output tokens'))
    if (spent.searches) {
      bits.push(plain(' · '), mono(`${spent.searches} web request${s(spent.searches)}`))
    }
    bits.push(plain('.'))
    blocks.push({ b: 'para', rich: bits })
    const est = estimateSpendUsd(session.spend)
    if (est.usd > 0) {
      blocks.push({
        b: 'para',
        rich: [
          plain(
            `That comes to about ${fmtUsd(est.usd)} at list prices read on ${PRICE_READ_ON}${est.unpriced.length ? `, not counting the passes on ${modelNames(est.unpriced)}, which ${est.unpriced.length === 1 ? 'has' : 'have'} no published rate here` : ''}. The estimate leans high by construction — each token is charged at the rate its kind bills at, anything a receipt did not break down at the full rate, and no discount or credit is counted — so the real charge was no higher and may have been lower. It is not a bill and cannot be reconciled against one.`,
          ),
        ],
      })
    }
    if (opts?.lens) {
      blocks.push({
        b: 'para',
        tone: 'quiet',
        rich: [plain(`Lens at export: ${opts.lens}. Temper: ${opts.temper ?? 'balanced'}.`)],
      })
    }
    appendix.push({
      id: 'provenance',
      title: 'How this was produced',
      eyebrow: 'PROVENANCE',
      blocks,
    })
  }

  /* The limits, last, where a reader arrives at them after the content. */
  appendix.push({
    id: 'limits',
    title: 'What this document does not establish',
    blocks: [
      {
        b: 'ledger',
        title: 'WHAT THIS DOCUMENT DOES NOT ESTABLISH',
        bullets: [
          [
            bold('Confidence, novelty and tension are the model’s own self-ratings'),
            plain(
              ', calibrated by instruction rather than measurement. They sort claims; they are not probabilities.' +
                (mixed
                  ? ' Two models reasoned here, on separate scales, so those numbers do not compare between claims produced by different ones.'
                  : ''),
            ),
          ],
          [
            bold('A reading is what a search returned on a date.'),
            plain(
              ` “${READING_LABEL.supported}” means the results that came back read as consistent with the claim. It is a record, not a proof.`,
            ),
          ],
          [
            bold('An absence of prior art is weak evidence.'),
            plain(' Where a check found nothing, that is a place to look rather than a discovery.'),
          ],
          [
            bold('An objection that stood and a path that failed are the model’s judgement'),
            plain(', in reasoning. Only a recorded result answers one.'),
          ],
          [
            bold('Nothing was omitted from this document by a display setting.'),
            plain(' The filters, the timeline and the folds in the app do not affect it.'),
          ],
        ],
      },
    ],
  })

  return {
    title: oneLine(session.seed),
    header,
    lede: REPORT_LEDE,
    preamble,
    sections,
    appendixIntro: APPENDIX_INTRO,
    appendix,
  }
}

/**
 * The sections and the appendix as one run of blocks, headings included — what
 * both formats print below their own title and header.
 *
 * A section whose content is the ledger takes no heading of its own: the PDF sets
 * the ledger's title inside its box, and the Markdown renderer turns that title
 * into the heading, so each format prints the name exactly once.
 */
export function reportBody(doc: ReportDoc): Block[] {
  const out: Block[] = []
  const push = (x: ReportSection, tone: 'accent' | 'quiet') => {
    if (x.eyebrow) out.push({ b: 'eyebrow', tone, text: x.eyebrow })
    if (x.blocks[0]?.b !== 'ledger') out.push({ b: 'h2', text: x.title })
    out.push(...x.blocks)
  }
  for (const x of doc.sections) push(x, 'accent')
  out.push({ b: 'eyebrow', tone: 'quiet', text: 'APPENDIX' })
  out.push({ b: 'h2', text: 'Appendix', part: true })
  out.push({ b: 'para', tone: 'quiet', rich: [plain(doc.appendixIntro)] })
  for (const x of doc.appendix) push(x, 'quiet')
  return out
}

/** Every heading the document prints, in order — the seam the two formats'
 *  test holds them to. */
export function reportTitles(doc: ReportDoc): string[] {
  return [
    ...doc.preamble.flatMap((b) => (b.b === 'h2' ? [b.text] : [])),
    ...doc.sections.map((x) => x.title),
    'Appendix',
    ...doc.appendix.map((x) => x.title),
  ]
}
